import { type ReactNode } from 'react';

import { Box, Stack, Typography } from '@mui/material';

import { Button } from '@karehero/llama/components/atoms/Button/Button';

import StatusBar from '@/designs-dev/device/StatusBar';

// The phone scaffold every Tell Us Once frame sits in: status bar, an optional
// title, then the body. Shared across the explorations' frames so dozens of
// sketches don't each re-derive the chrome and drift apart while they're being
// compared side by side. It sits one level up from the exploration folders
// because both of them build on it.
//
// Back sits at the bottom with the other actions, not as an arrow above the
// title: going back is a choice you make after reading the question, so it
// belongs where the rest of the choices are.
export type ScreenProps = {
  title?: string;
  /** Shown under the title — a progress hint or a quiet subheading. */
  subtitle?: string;
  back?: boolean;
  /** Pinned to the bottom of the screen, outside the scrolling body. */
  footer?: ReactNode;
  children: ReactNode;
};

const Screen = ({ title, subtitle, back, footer, children }: ScreenProps) => (
  <Stack sx={{ height: '100%', bgcolor: 'background.default' }}>
    <StatusBar />

    {title && (
      <Stack sx={{ px: 3, pb: 2 }} spacing={0.5}>
        <Typography variant='h3'>{title}</Typography>
        {subtitle && (
          <Typography variant='body2' color='text.secondary'>
            {subtitle}
          </Typography>
        )}
      </Stack>
    )}

    <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', px: 3 }}>
      {children}
    </Box>

    {(footer || back) && (
      // spacing={2} is 16px inside a phone frame — the standard gap between
      // adjacent buttons, so Back sits off the primary action by the same
      // amount everything else in the system does. (The frames run under the
      // designs app's linear 8px override, not llama's token array, so this is
      // 2 rather than the 3 that would give 16px in the real app.)
      <Stack sx={{ px: 3, pt: 2, pb: 4 }} spacing={2}>
        {footer}
        {back && (
          <Button
            ariaLabel='Back'
            variant='link'
            isFullWidth
            iconLeft={'long-arrow-left' as never}
          >
            Back
          </Button>
        )}
      </Stack>
    )}
  </Stack>
);

Screen.displayName = 'Screen';
export default Screen;
